import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookOpen,
  Clock3,
  FileText,
  Loader2,
  Trophy,
} from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getArrayFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.mockTests)) return result.mockTests;
  if (Array.isArray(data?.mockTests)) return data.mockTests;
  if (Array.isArray(data)) return data;

  return [];
};

const getObjectFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (result && !Array.isArray(result) && typeof result === "object") {
    return result?.mockTest || result;
  }
  if (data && !Array.isArray(data) && typeof data === "object") {
    return data?.mockTest || data;
  }

  return null;
};

const getCourseId = (mockTest) =>
  pickFirst(
    mockTest?.course?._id,
    mockTest?.courseId?._id,
    mockTest?.courseId,
    mockTest?.course,
    mockTest?.plan?._id,
    mockTest?.planId,
    mockTest?._id,
  );

const getCategory = (mockTest) =>
  pickFirst(
    mockTest?.category,
    mockTest?.course?.planName,
    mockTest?.courseName,
    mockTest?.examName,
    mockTest?.subject,
    "Mock Test",
  );

const getLanguages = (mockTest) => {
  const languages = pickFirst(mockTest?.languages, mockTest?.language);

  if (Array.isArray(languages)) return languages.filter(Boolean);
  if (languages) return [languages];

  return ["English"];
};

const MockTestDetails = () => {
  const navigate = useNavigate();
  const { mockTestId } = useParams();
  const [mockTest, setMockTest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMockTest = async () => {
      setLoading(true);

      try {
        try {
          const response = await axios.get(`/mock-tests/${mockTestId}`);
          const detail = getObjectFromResponse(response);

          if (detail?._id || detail?.title) {
            setMockTest(detail);
            return;
          }
        } catch {
          // Fallback below keeps this page compatible with list-only APIs.
        }

        const response = await axios.get("/mock-tests/all");
        const tests = getArrayFromResponse(response);
        const detail = tests.find((item) => String(item?._id) === String(mockTestId));

        if (!detail) {
          toast.error("Mock test details were not found");
        }

        setMockTest(detail || null);
      } catch (error) {
        toast.error("Unable to load mock test details");
      } finally {
        setLoading(false);
      }
    };

    fetchMockTest();
  }, [mockTestId]);

  const detail = useMemo(() => {
    const totalQuestions = pickFirst(
      mockTest?.totalQuestions,
      mockTest?.questionCount,
      mockTest?.questions?.length,
      0,
    );
    const duration = pickFirst(
      mockTest?.durationMinutes,
      mockTest?.duration,
      mockTest?.timeLimit,
      0,
    );
    const marks = pickFirst(
      mockTest?.totalMarks,
      mockTest?.marks,
      mockTest?.maxMarks,
      totalQuestions,
    );
    const marksPerQuestion =
      Number(totalQuestions) > 0
        ? Number(marks || 0) / Number(totalQuestions)
        : Number(marks || 0);
    const sections =
      Array.isArray(mockTest?.subjectDistribution) &&
      mockTest.subjectDistribution.length
        ? mockTest.subjectDistribution.map((item) => ({
            name: item?.subject || item?.name || "Subject",
            questions: item?.questionCount || item?.totalQuestions || 0,
          }))
        : getLanguages(mockTest).map((language) => ({
            name: language,
            questions: totalQuestions,
          }));

    return {
      title: pickFirst(mockTest?.title, mockTest?.name, mockTest?.mockTestName, "Mock Test"),
      description: pickFirst(mockTest?.description, mockTest?.subTitle, "Mock Test"),
      category: getCategory(mockTest),
      totalQuestions,
      duration,
      marks,
      attempts: pickFirst(mockTest?.attempts, mockTest?.attemptCount, mockTest?.myAttempts, 0),
      languages: getLanguages(mockTest),
      sections,
      marksPerQuestion: Number.isInteger(marksPerQuestion)
        ? marksPerQuestion
        : marksPerQuestion.toFixed(1),
      courseId: getCourseId(mockTest),
    };
  }, [mockTest]);

  const handleStart = () => {
    navigate(`/dash/mock-test/${detail.courseId}?start=${mockTest?._id || mockTestId}`);
  };

  const handlePrimaryAction = () => {
    if (mockTest?.isSubmitted) {
      navigate(`/dash/mock-test-result/${mockTest?._id || mockTestId}`);
      return;
    }

    handleStart();
  };

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      {/* <header className="sticky top-0 z-30 border-b border-[#eee8f0] bg-white/95 px-4 py-4 shadow-sm backdrop-blur lg:static lg:px-8">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f7f5f8] text-[#211629] shadow-sm transition hover:scale-95"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="truncate text-base font-semibold text-[#211629] sm:text-xl">
              Mock Test Details
            </h1>
          </div>

          <button
            type="button"
            onClick={() => navigate("/dash/wallet")}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#f7f1f8] text-[#562566] shadow-sm"
            aria-label="Wallet"
          >
            <Wallet className="h-5 w-5" />
          </button>
        </div>
      </header> */}

      <main className="mx-auto lg:mt-14 max-w-5xl px-4 py-5 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-3xl bg-white shadow-sm">
            <Loader2 className="h-8 w-8 animate-spin text-[#562566]" />
          </div>
        ) : mockTest ? (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
            <div className="space-y-5">
              <section className="rounded-[1.5rem] bg-[#562566] p-4 text-left text-white shadow-[0_14px_28px_rgba(86,37,102,0.16)] sm:p-6">
                <p className="text-xs font-bold text-white/80">
                  {detail.category}
                </p>
                <h2 className="mt-1 text-2xl font-bold leading-tight sm:text-3xl">
                  {detail.title}
                </h2>
                <p className="mt-2 text-sm font-medium leading-6 text-white/82">
                  {detail.description}
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <span className="inline-flex items-center justify-center gap-1 rounded-full bg-white/12 px-2 py-2 text-[11px] font-bold sm:gap-2 sm:text-sm">
                    <FileText className="h-4 w-4 shrink-0" />
                    {detail.totalQuestions} Questions
                  </span>
                  <span className="inline-flex items-center justify-center gap-1 rounded-full bg-white/12 px-2 py-2 text-[11px] font-bold sm:gap-2 sm:text-sm">
                    <Clock3 className="h-4 w-4 shrink-0" />
                    {detail.duration} min
                  </span>
                  <span className="inline-flex items-center justify-center gap-1 rounded-full bg-white/12 px-2 py-2 text-[11px] font-bold sm:gap-2 sm:text-sm">
                    <Award className="h-4 w-4 shrink-0" />
                    {detail.marks} Marks
                  </span>
                </div>
              </section>

              <button
                type="button"
                onClick={() =>
                  navigate(`/dash/mock-test-leaderboard/${mockTest?._id || mockTestId}`)
                }
                className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full border border-[#eadfee] bg-[#fbf5fd] text-sm font-extrabold text-[#562566] shadow-sm"
              >
                View Leaderboard
                <Trophy className="h-4 w-4" />
              </button>

              <section className="rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm sm:p-5 lg:hidden">
                <h3 className="text-base font-extrabold text-[#211629]">
                  Sections
                </h3>
                <div className="mt-4 space-y-3">
                  {detail.sections.map((section, index) => (
                    <div
                      key={`${section.name}-${index}`}
                      className="rounded-[1.4rem] bg-[#f7f2f8] px-4 py-4 text-sm font-semibold text-[#6e6875]"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-bold text-[#211629]">
                            {section.name}
                          </p>
                          <p className="mt-1 text-xs font-medium text-[#6e6875]">
                            {section.name}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-[#6e6875]">
                          {section.questions} Questions
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-3 text-xs font-medium text-[#6e6875]">
                        <span>{section.questions} Questions</span>
                        <span className="text-center">{detail.duration} min</span>
                        <span className="text-right">
                          {detail.marksPerQuestion} marks/q
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm sm:p-5">
                <h3 className="text-base font-extrabold text-[#211629]">
                  Instructions
                </h3>
                <ul className="mt-4 space-y-3 text-sm font-medium leading-6 text-[#6e6875]">
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#562566]" />
                    You will have {detail.duration} minutes to complete this mock test.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#562566]" />
                    Read each question carefully before selecting your answer.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#562566]" />
                    You can review and change your answers before final submission.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#562566]" />
                    Skipped questions will be counted as unanswered.
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#562566]" />
                    Make sure to submit before the timer ends.
                  </li>
                </ul>
              </section>
            </div>

            <aside className="space-y-5">
              <section className="hidden rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm sm:p-5 lg:block">
                <h3 className="text-base font-extrabold text-[#211629]">
                  Sections
                </h3>
                <div className="mt-4 space-y-3">
                  {detail.sections.map((section, index) => (
                    <div
                      key={`${section.name}-${index}`}
                      className="rounded-[1.4rem] bg-[#f7f2f8] px-4 py-4 text-sm font-semibold text-[#6e6875]"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-bold text-[#211629]">
                            {section.name}
                          </p>
                          <p className="mt-1 text-xs font-medium text-[#6e6875]">
                            {section.name}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-[#6e6875]">
                          {section.questions} Questions
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-3 text-xs font-medium text-[#6e6875]">
                        <span>{section.questions} Questions</span>
                        <span className="text-center">{detail.duration} min</span>
                        <span className="text-right">
                          {detail.marksPerQuestion} marks/q
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <button
                type="button"
                onClick={handlePrimaryAction}
                className={`hidden h-14 w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-bold text-white transition hover:scale-[0.98] lg:inline-flex ${
                  mockTest?.isSubmitted ? "bg-[#562566]" : "bg-[#F5A623]"
                }`}
              >
                {mockTest?.isSubmitted ? "View Report" : "Start Mock Test"}
                <ArrowRight className="h-5 w-5" />
              </button>
            </aside>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#eee8f0] bg-white p-8 text-center shadow-sm">
            <BookOpen className="mx-auto h-10 w-10 text-[#562566]" />
            <h2 className="mt-3 text-xl font-extrabold">Mock test not found</h2>
          </div>
        )}
      </main>

      {mockTest && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#eee8f0] px-4 py-3 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={handlePrimaryAction}
            className={`flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-extrabold text-white shadow-[0_10px_20px_rgba(86,37,102,0.18)] ${
              mockTest?.isSubmitted ? "bg-[#562566]" : "bg-[#F5A623]"
            }`}
          >
            {mockTest?.isSubmitted ? "View Report" : "Start Mock Test"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MockTestDetails;
